import { useEffect, useState } from "react";
import { Calculator, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { inr } from "@/lib/format";
import { useStore } from "@/context/StoreContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/* Old-gold exchange ESTIMATOR — a lead tool, not a transaction. It never commits
   the business to a price: the output is indicative only, computed from the live
   rate, and final value is determined after physical purity & weight testing. */

const PURITIES = [
  { key: "24K", frac: 0.999, label: "24K (99.9%)" },
  { key: "22K", frac: 0.916, label: "22K (91.6%)" },
  { key: "18K", frac: 0.75, label: "18K (75%)" },
  { key: "14K", frac: 0.585, label: "14K (58.5%)" },
  { key: "9K", frac: 0.375, label: "9K (37.5%)" },
];

export default function GoldExchange() {
  const { config } = useStore();
  const [rate, setRate] = useState(null);
  const [weight, setWeight] = useState("");
  const [purity, setPurity] = useState("22K");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    document.title = "Old Gold Exchange Value — Indicative Calculator | Siyaara";
    api.get("/rates/current").then((r) => setRate(r.data)).catch(() => {});
  }, []);

  const p = PURITIES.find((x) => x.key === purity);
  const deduction = config?.old_gold_deduction_pct || 0;
  const w = parseFloat(weight) || 0;
  const gross = rate && w > 0 ? w * rate.gold_24k * p.frac : 0;
  const value = gross * (1 - deduction / 100);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) { toast.error("Name and phone are required"); return; }
    if (!consent) { toast.error("Please tick the consent box so we can call you back"); return; }
    setBusy(true);
    try {
      await api.post("/enquiries/old-gold", {
        name: name.trim(), phone: phone.trim(), consent,
        weight_grams: w, purity, indicative_value: Math.round(value),
        rate_24k: rate?.gold_24k, deduction_pct: deduction,
      });
      toast.success("Thank you — our team will call you to arrange a free, no-obligation evaluation.");
      setName(""); setPhone(""); setConsent(false);
    } catch { toast.error("Could not submit — please try again"); }
    finally { setBusy(false); }
  };

  return (
    <div className="container-x py-12 max-w-3xl" data-testid="gold-exchange-page">
      <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-gold">Old gold exchange</p>
      <h1 className="font-heading text-4xl sm:text-5xl font-semibold mt-2">What is your old gold worth today?</h1>
      <p className="text-stone-600 mt-3 max-w-2xl">
        An instant, indicative figure from the live gold rate. Bring the piece in — the final value is
        determined only after physical purity and weight testing in front of you.
      </p>

      <div className="mt-8 border border-stone-200 bg-white p-6 sm:p-8" data-testid="exchange-calculator">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="eyebrow">Weight (grams)</label>
            <Input type="number" min="0" step="0.01" value={weight} onChange={(e) => setWeight(e.target.value)}
              placeholder="e.g. 10" className="mt-1.5" data-testid="exchange-weight" />
          </div>
          <div>
            <label className="eyebrow">Purity</label>
            <div className="mt-1.5 flex flex-wrap gap-2" data-testid="exchange-purity">
              {PURITIES.map((x) => (
                <button key={x.key} type="button" onClick={() => setPurity(x.key)}
                  className={`px-3 py-2 text-xs border rounded-full transition-colors ${purity === x.key ? "border-brand bg-brand text-ivory" : "border-stone-300 hover:border-brand"}`}
                  data-testid={`exchange-purity-${x.key}`}>{x.label}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 bg-brand text-ivory p-6 text-center">
          <p className="eyebrow !text-gold">Indicative value today</p>
          <p className="font-heading text-4xl font-semibold mt-2" data-testid="exchange-value">
            {value > 0 ? inr(value) : "—"}
          </p>
          {rate && <p className="text-[11px] text-ivory/60 mt-2 font-mono">24K rate {inr(rate.gold_24k)}/g · updated {new Date(rate.updated_at).toLocaleString("en-IN")}</p>}
          {deduction > 0 && <p className="text-[11px] text-ivory/60 mt-1">After a {deduction}% evaluation deduction</p>}
        </div>

        <div className="mt-4 flex items-start gap-2 text-xs text-stone-500">
          <ShieldAlert size={14} className="text-gold shrink-0 mt-0.5" />
          <p data-testid="exchange-disclaimer">
            This is an indicative estimate only, computed from the live gold rate. It is not an offer and not a
            guaranteed rate. The final value is determined after physical purity and weight testing at our store,
            and may differ from this figure.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-6 border border-stone-200 bg-white p-6 sm:p-8" data-testid="exchange-lead-form">
        <p className="eyebrow mb-1">Get a firm evaluation</p>
        <p className="text-sm text-stone-600 mb-4">Leave your details and we will call you to arrange a free testing appointment — no obligation to sell.</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" data-testid="exchange-name" />
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (e.g. +91 98765 43210)" data-testid="exchange-phone" />
        </div>
        <label className="mt-3 flex items-start gap-2 text-xs text-stone-600 cursor-pointer">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" data-testid="exchange-consent" />
          <span>I agree to Siyaara contacting me about this enquiry. My details are used only for this conversation.</span>
        </label>
        <Button type="submit" disabled={busy} className="mt-4 bg-brand text-ivory hover:bg-brand-2 rounded-full px-8 btn-press" data-testid="exchange-submit">
          {busy ? "Sending…" : "Request a callback"}
        </Button>
      </form>
    </div>
  );
}
